import * as api from "@/api/article";
import { Toast } from "@/components/ui/toast";
import { authService } from "@/services/AuthService";
import { i18n } from "@/utils/i18n";

/** up-data 传入的收藏配置 */
export interface Data {
  articleId: number;
  isLoggedIn: boolean;
  favorited: boolean;
  favoriteCount: number;
}

/**
 * ArticleFavorite —— 文章收藏 UI 组件
 *
 * 职责：监听点击事件、调用收藏接口、渲染 UI 状态。
 *
 * 未登录时：弹出登录 Modal，登录成功后以 set 语义执行收藏。
 */
export class Favorite {
  private favorited: boolean;
  private favoriteCount: number;
  private favoriting = false;

  private btn: HTMLButtonElement;
  private iconSvg: SVGElement | null;
  private countEl: HTMLElement | null;

  constructor(
    element: HTMLElement,
    private data: Data,
  ) {
    this.favorited = data.favorited;
    this.favoriteCount = data.favoriteCount;

    this.btn = element as HTMLButtonElement;
    this.iconSvg = element.querySelector("svg");
    this.countEl = element.querySelector("[data-favorite-count]");

    this.init();
  }

  private init(): void {
    this.btn.addEventListener("click", this.toggle);
    this.render();
  }

  /**
   * 收藏 toggle
   */
  private toggle = async (): Promise<void> => {
    if (this.favoriting) return;

    if (!authService.isSignedIn) {
      authService.signin({
        onAccepted: async () => {
          // 登录后用 set 语义，确保收藏成功
          await this.perform("set");
        },
        onDismissed: () => {
          // 取消登录，不做操作
        },
      });
      return;
    }

    // 已登录用户正常 toggle
    await this.perform("toggle");
  };

  /**
   * 执行收藏网络请求
   */
  private async perform(action: "set" | "toggle"): Promise<void> {
    this.favoriting = true;
    this.setLoading(true);

    try {
      const res = await api.favorite(this.data.articleId, action);
      this.favorited = res.favorited;
      this.favoriteCount = res.favoriteCount;
      this.render();
    } catch (_err) {
      Toast.error(i18n.t("article.favorite_failed"));
    } finally {
      this.favoriting = false;
      this.setLoading(false);
    }
  }

  private setLoading(loading: boolean): void {
    this.btn.disabled = loading;
    this.btn.classList.toggle("opacity-50", loading);
    this.btn.classList.toggle("cursor-not-allowed", loading);
  }

  /**
   * 根据当前状态更新 DOM
   */
  private render(): void {
    /* 计数文本 */
    if (this.countEl) {
      this.countEl.textContent = String(this.favoriteCount);
    }

    /* 按钮样式 */
    this.btn.classList.toggle("text-primary", this.favorited);
    this.btn.classList.toggle(
      "text-muted",
      !this.favorited && !this.btn.classList.contains("text-muted-light"),
    );
    this.btn.classList.toggle(
      "text-muted-light",
      !this.favorited && this.btn.dataset.mutedClass === "muted-light",
    );

    /* 图标样式 */
    if (this.iconSvg) {
      this.iconSvg.classList.toggle("text-primary", this.favorited);
      this.iconSvg.classList.toggle("fill-current", this.favorited);
      this.iconSvg.classList.toggle("scale-110", this.favorited);
    }

    /* disabled 状态 */
    // this.btn.classList.toggle('opacity-50', this.favoriting);
    this.btn.classList.toggle("cursor-not-allowed", this.favoriting);
  }

  destroy(): void {
    this.btn.removeEventListener("click", this.toggle);
  }

  /** 注册 Unpoly 编译器 */
  static compiler(selector: string): void {
    up.compiler(selector, (element: Element, data: Data) => {
      const favorite = new Favorite(element as HTMLElement, data);
      return () => favorite.destroy();
    });
  }
}
